import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Logo } from "@/components/Logo";
import {
  Brain, FileText, Calculator, ShieldCheck, Zap, ArrowRight,
  CheckCircle2,
} from "lucide-react";

export default function Landing() {
  const navigate = useNavigate();

  const features = [
    {
      icon: Brain,
      title: "TAKT. reasoning",
      desc: "Plans are parsed by a senior-estimator-grade AI that reads schedules, dimensions, and specs the way you do.",
    },
    {
      icon: Calculator,
      title: "Division-by-division breakdown",
      desc: "CSI MasterFormat divisions with line items for materials, labor, equipment — not vague lump sums.",
    },
    {
      icon: FileText,
      title: "PDF in. Estimate out.",
      desc: "Drag in a residential plan set. In under a minute, get a defensible cost sheet you can hand to a client.",
    },
    {
      icon: Zap,
      title: "Editable & exportable",
      desc: "Tune line items, markups, overhead and profit. Your estimate, your numbers — accelerated by AI.",
    },
  ];

  const steps = [
    { n: "01", title: "Upload plans", desc: "Drop in a residential PDF (single sheets or a full set)." },
    { n: "02", title: "AI reads the set", desc: "Sqft, room count, materials, scope — extracted in seconds." },
    { n: "03", title: "Get a line-item estimate", desc: "Divisions, quantities, unit costs, overhead and profit." },
  ];

  return (
    <div className="min-h-screen bg-[#0F1115] text-slate-200" style={{ fontFamily: "'IBM Plex Sans', sans-serif" }}>
      {/* Header */}
      <header className="fixed top-0 inset-x-0 z-30 backdrop-blur-xl bg-[#0F1115]/80 border-b border-[#2A2F38]">
        <div className="max-w-7xl mx-auto flex items-center justify-between px-6 md:px-12 py-4">
          <div data-testid="landing-brand">
            <Logo size="md" />
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate("/login")}
              className="text-sm text-slate-300 hover:text-white px-4 py-2 transition-colors"
              data-testid="header-login-link"
            >
              Sign in
            </button>
            <Button
              onClick={() => navigate("/login?mode=register")}
              className="bg-[#FF6A00] hover:bg-[#E65C00] text-white rounded-md"
              data-testid="header-get-started-btn"
            >
              Get started
            </Button>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="relative pt-32 pb-20 md:pt-48 md:pb-32 overflow-hidden">
        <div
          className="absolute inset-0 -z-10"
          style={{
            backgroundImage:
              "url('https://images.unsplash.com/photo-1599707254554-027aeb4deacd?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODl8MHwxfHNlYXJjaHwxfHxtb2Rlcm4lMjBjb25zdHJ1Y3Rpb24lMjBzaXRlJTIwYnVpbGRpbmd8ZW58MHx8fHwxNzgxNTc5NzY4fDA&ixlib=rb-4.1.0&q=85')",
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
        />
        <div className="absolute inset-0 -z-10 bg-gradient-to-r from-[#0F1115] via-[#0F1115]/90 to-[#0F1115]/40" />
        <div className="absolute inset-0 -z-10 bg-gradient-to-b from-transparent via-transparent to-[#0F1115]" />

        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="max-w-3xl"
          >
            <div className="inline-flex items-center gap-2 px-3 py-1.5 border border-[#2A2F38] bg-[#171A20] text-xs uppercase tracking-[0.2em] text-slate-300 mb-6" data-testid="hero-eyebrow">
              <span className="h-1.5 w-1.5 rounded-full bg-[#FF6A00]" />
              For residential GCs & remodelers
            </div>
            <h1
              className="text-4xl sm:text-5xl lg:text-7xl tracking-tighter font-black text-white leading-[0.95]"
              style={{ fontFamily: "Chivo, sans-serif" }}
              data-testid="hero-heading"
            >
              Estimate a house
              <br />
              <span className="text-[#FF6A00]">before lunch.</span>
            </h1>
            <p className="mt-6 text-lg md:text-xl text-slate-300 leading-relaxed max-w-2xl">
              Drop in a set of residential plans. TAKT. reads them with a senior-estimator-grade
              AI and returns a line-item estimate — materials, labor, equipment, overhead and profit.
            </p>
            <div className="mt-10 flex flex-wrap items-center gap-4">
              <Button
                size="lg"
                onClick={() => navigate("/login?mode=register")}
                className="bg-[#FF6A00] hover:bg-[#E65C00] text-white rounded-md px-8 py-6 text-base"
                data-testid="hero-cta-primary"
              >
                Estimate my first plan
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
              <Button
                size="lg"
                variant="outline"
                onClick={() => navigate("/login")}
                className="bg-transparent border-[#2A2F38] hover:bg-[#171A20] text-slate-200 rounded-md px-8 py-6 text-base"
                data-testid="hero-cta-secondary"
              >
                Sign in
              </Button>
            </div>
            <div className="mt-12 grid grid-cols-3 gap-px bg-[#2A2F38] border border-[#2A2F38] max-w-2xl">
              {[
                { v: "60 sec", l: "Avg. estimate" },
                { v: "12+", l: "CSI divisions" },
                { v: "Editable", l: "Every line" },
              ].map((s) => (
                <div key={s.l} className="bg-[#0F1115] px-5 py-4">
                  <div
                    className="text-2xl font-black text-white"
                    style={{ fontFamily: "JetBrains Mono, monospace" }}
                  >
                    {s.v}
                  </div>
                  <div className="text-[10px] tracking-[0.25em] uppercase text-slate-500 mt-1">
                    {s.l}
                  </div>
                </div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* How it works */}
      <section className="border-t border-[#2A2F38] py-24 md:py-32" data-testid="how-it-works">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-[10px] tracking-[0.3em] uppercase text-[#FF6A00] font-semibold mb-4">
            How it works
          </div>
          <h2
            className="text-3xl md:text-5xl font-bold tracking-tight text-white max-w-2xl"
            style={{ fontFamily: "Chivo, sans-serif" }}
          >
            From PDF to bid in three steps.
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-[#2A2F38] border border-[#2A2F38] mt-16">
            {steps.map((s) => (
              <div key={s.n} className="bg-[#0F1115] p-8 md:p-10">
                <div
                  className="text-5xl font-black text-[#FF6A00]"
                  style={{ fontFamily: "JetBrains Mono, monospace" }}
                >
                  {s.n}
                </div>
                <h3
                  className="mt-6 text-xl font-bold text-white"
                  style={{ fontFamily: "Chivo, sans-serif" }}
                >
                  {s.title}
                </h3>
                <p className="mt-3 text-slate-400 leading-relaxed">{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="border-t border-[#2A2F38] py-24 md:py-32" data-testid="features-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid md:grid-cols-2 gap-16">
            <div>
              <div className="text-[10px] tracking-[0.3em] uppercase text-[#FF6A00] font-semibold mb-4">
                Built for builders
              </div>
              <h2
                className="text-3xl md:text-5xl font-bold tracking-tight text-white"
                style={{ fontFamily: "Chivo, sans-serif" }}
              >
                Numbers you can actually defend.
              </h2>
              <p className="mt-6 text-slate-300 leading-relaxed text-lg">
                Most estimating "tools" are spreadsheets in disguise. TAKT. reads the plan and
                writes the take-off — so you can spend your time pricing, not measuring.
              </p>
              <div className="mt-10 space-y-3">
                {[
                  "Reads schedules and floor-plan dimensions",
                  "Per-line labor, materials, equipment splits",
                  "Configurable overhead & profit margins",
                  "Every line is editable before you send the bid",
                ].map((b) => (
                  <div key={b} className="flex items-start gap-3">
                    <CheckCircle2 className="h-5 w-5 text-[#FF6A00] mt-0.5 flex-shrink-0" />
                    <span className="text-slate-300">{b}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-px bg-[#2A2F38] border border-[#2A2F38]">
              {features.map((f) => (
                <div key={f.title} className="bg-[#0F1115] p-6">
                  <f.icon className="h-6 w-6 text-[#FF6A00]" />
                  <h3
                    className="mt-4 text-base font-bold text-white"
                    style={{ fontFamily: "Chivo, sans-serif" }}
                  >
                    {f.title}
                  </h3>
                  <p className="mt-2 text-sm text-slate-400 leading-relaxed">{f.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="border-t border-[#2A2F38] py-24 md:py-32">
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
          <ShieldCheck className="h-10 w-10 mx-auto text-[#FF6A00]" />
          <h2
            className="mt-6 text-3xl md:text-5xl font-bold tracking-tight text-white"
            style={{ fontFamily: "Chivo, sans-serif" }}
          >
            Stop wrestling spreadsheets.
          </h2>
          <p className="mt-4 text-slate-400 text-lg max-w-2xl mx-auto">
            Upload your first plan free. No card, no setup. Get a complete estimate in under a minute.
          </p>
          <Button
            size="lg"
            onClick={() => navigate("/login?mode=register")}
            className="mt-10 bg-[#FF6A00] hover:bg-[#E65C00] text-white rounded-md px-8 py-6"
            data-testid="cta-final-btn"
          >
            Start estimating
            <ArrowRight className="ml-2 h-4 w-4" />
          </Button>
        </div>
      </section>

      <footer className="border-t border-[#2A2F38] py-10">
        <div className="max-w-7xl mx-auto px-6 md:px-12 flex flex-col md:flex-row md:items-center md:justify-between gap-4 text-sm text-slate-500">
          <div>© {new Date().getFullYear()} TAKT. — Built for residential contractors.</div>
          <div className="flex gap-6">
            <span className="hover:text-slate-300 transition-colors cursor-default">Privacy</span>
            <span className="hover:text-slate-300 transition-colors cursor-default">Terms</span>
            <span className="hover:text-slate-300 transition-colors cursor-default">Contact</span>
          </div>
        </div>
      </footer>
    </div>
  );
}
